import i18n, { type TFunction } from 'i18next';
import type { FC, PropsWithChildren } from 'react';
import { I18nextProvider, initReactI18next } from 'react-i18next';
import { type ZodErrorMap, ZodIssueCode, z } from 'zod';
import { zodI18nMap } from 'zod-i18n-map';
import generalTranslationEN from './locales/en/general_translation.json';
import generalZodEN from './locales/en/general_zod.json';
import generalTranslationJA from './locales/ja/general_translation.json';
import generalZodJA from './locales/ja/general_zod.json';

declare module 'i18next' {
  interface CustomTypeOptions {
    returnNull: false;
  }
}

/**
 * @description It doesn't change because dots are used in zod-i18n-map.
 * @see https://www.i18next.com/overview/configuration-options#languages-namespaces-resources#:~:text=keySeparator
 * @default '.'
 */
const KEY_SEPARATOR = '.';

const resources = {
  ja: {
    translation: generalTranslationJA,
    zod: generalZodJA,
  },
  en: {
    translation: generalTranslationEN,
    zod: generalZodEN,
  },
};

// Get saved language from localStorage or default to Japanese
const getInitialLanguage = (): string => {
  const savedLanguage = localStorage.getItem('language');
  return savedLanguage && ['ja', 'en'].includes(savedLanguage)
    ? savedLanguage
    : 'ja';
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getInitialLanguage(),
    fallbackLng: 'ja',
    returnNull: false,
    keySeparator: KEY_SEPARATOR,
    interpolation: {
      escapeValue: false,
    },
  })
  .catch((e) => {
    throw e;
  });

const errorMap: ZodErrorMap = (issue, _ctx) => {
  let message = '';
  switch (issue.code) {
    case ZodIssueCode.invalid_string:
      // This validation would be only for selecting UUID.
      if (issue.validation === 'uuid') {
        message = t('message-must-select');
      }
      break;
    case ZodIssueCode.too_small:
      if (issue.type === 'array') {
        if (issue.minimum === 1 && issue.inclusive) {
          message = t('validation.required');
        }
      }
      if (issue.type === 'string') {
        if (issue.minimum === 1 && issue.inclusive && !issue.exact) {
          message = t('validation.required');
        }
      }
      if (issue.type === 'number') {
        const commaSeparatedMinimumValue = issue.minimum.toLocaleString();
        message = issue.inclusive
          ? t('validation.number.too-small-inclusive', {
              minimum: commaSeparatedMinimumValue,
            })
          : t('validation.number.too-small-not-inclusive', {
              minimum: commaSeparatedMinimumValue,
            });
      }
      break;
    case ZodIssueCode.too_big:
      if (issue.type === 'number') {
        const commaSeparatedMaximumValue = issue.maximum.toLocaleString();
        message = issue.inclusive
          ? t('validation.number.too-big-inclusive', {
              maximum: commaSeparatedMaximumValue,
            })
          : t('validation.number.too-big-not-inclusive', {
              maximum: commaSeparatedMaximumValue,
            });
      }
  }
  return message ? { message } : zodI18nMap(issue, _ctx);
};
z.setErrorMap(errorMap);

const { t, addResource, changeLanguage } = i18n;

// Language switching function
export const switchLanguage = (language: 'ja' | 'en') => {
  localStorage.setItem('language', language);
  changeLanguage(language);
};

// Get current language
export const getCurrentLanguage = (): string => i18n.language;

const _overrideI18n = (resources: any, isKeySeparatorEnable = true) => {
  // https://github.com/i18next/i18next/blob/dcf1922e6c415a55344e0e1cebde03d1b0786696/src/ResourceStore.js#L89C1-L92C6
  for (const key in resources) {
    const resource = resources[key];
    if (typeof resource === 'string' || Array.isArray(resource)) {
      addResource('ja', 'translation', key, resource as string, {
        silent: true,
        keySeparator: isKeySeparatorEnable ? KEY_SEPARATOR : '',
      });
    }
  }
};

export const I18nProvider: FC<PropsWithChildren> = ({ children }) => {
  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
};

export { t, type TFunction };
